import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { TopoBackground } from '@/components/layout/TopoBackground';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useJsonLd, useSeoMeta } from '@/hooks/useSeoMeta';
import { countriesQuery } from '@/data';
import {
  clearQuizState,
  EmailGate,
  hasEmailBeenCaptured,
  loadQuizState,
  QUESTIONS,
  QuestionBackdrop,
  QuestionStep,
  QuizProgress,
  saveQuizState,
  scoreCountries,
  SocialProofInterstitial,
  LoadingScreen,
  useQuizStore,
  usePassportTreatment,
  useCountryVisaExtra,
  useCountryTaxRegimes,
  useCostOfLiving,
} from '@/features/quiz';
import { useSession } from '@/lib/auth-client';
import { quizState as quizStateApi } from '@/lib/api';
import { LodestarLogo } from '@/components/brand';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';

// QuizPage композирует: progress bar, текущий QuestionStep, mid-quiz social-proof
// interstitial (между Q4 и Q5), 3-секундный loader перед редиректом на /quiz/result.
// Стиль — Quiet Atlas: paper bg, ink-100 borders, serif headings, mono progress line.

type Phase = 'questions' | 'interstitial' | 'loading' | 'email-gate';

// Question index after which we show the social-proof interstitial.
// Order: 0 horizon · 1 passport · 2 climate · 3 urban_density · 4 budget · 5 priorities · 6 income · 7 family · 8 languages · 9 dealbreakers.
// Interstitial fires between priorities (index 5) and income (index 6).
const INTERSTITIAL_AFTER_STEP = 5;

/** Canonical host — must match the origin build-sitemap.mjs advertises. */
const PROD_ORIGIN = 'https://lodestar.place';

export default function QuizPage() {
  useDocumentLang();
  const { t, i18n } = useTranslation();
  const lp = useLocalizedPath();

  // /quiz has no `/<lang>` twin — it renders bilingual at a single URL, and
  // build-sitemap.mjs lists it without alternates. So: self-referential
  // canonical, no hreflang set. Without this the prerendered snapshot kept
  // index.html's shell <title> and no canonical at all, which Google resolves
  // to the homepage — the product's main conversion page never got indexed.
  useSeoMeta({
    title: t('quiz.meta.title'),
    description: t('quiz.meta.description'),
    canonical: `${PROD_ORIGIN}/quiz`,
    locale: i18n.language === 'ru' ? 'ru_RU' : 'en_US',
  });
  useJsonLd(
    JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: t('app.title'), item: `${PROD_ORIGIN}/` },
        { '@type': 'ListItem', position: 2, name: t('quiz.meta.title'), item: `${PROD_ORIGIN}/quiz` },
      ],
    }),
    'quiz-breadcrumb',
  );

  const navigate = useNavigate();
  const { data: countriesFile } = useQuery(countriesQuery());
  // Stage 4.7 — preload de-facto passport-treatment dataset so it's warm by
  // the time scoreCountries() runs in handleLoadingDone(). One-shot fetch,
  // 24h staleTime — see passport-treatment.ts.
  const { data: passportTreatment } = usePassportTreatment();
  // Stage 5.0 — preload visa pathway and tax regime datasets. Both are
  // small (<130 KB), cache indefinitely. If not resolved by scoring time,
  // scoreCountries() falls back gracefully to legacy boolean-flag scoring.
  const { data: visaExtra } = useCountryVisaExtra();
  const { data: taxRegimes } = useCountryTaxRegimes();
  const { data: costOfLiving } = useCostOfLiving();

  const currentStep = useQuizStore((s) => s.currentStep);
  const answers = useQuizStore((s) => s.answers);
  const startedAt = useQuizStore((s) => s.startedAt);
  const result = useQuizStore((s) => s.result);
  const setResult = useQuizStore((s) => s.setResult);
  const restoreFromPersistence = useQuizStore((s) => s.restoreFromPersistence);

  const [phase, setPhase] = useState<Phase>('questions');
  const prevStepRef = useRef<number | null>(null);

  // Hydrate store from localStorage synchronously on mount.
  // Only restore in-progress state (result === null). A completed quiz
  // (result present) means the user finished and should start fresh —
  // this covers the edge case where clearQuizState races with the
  // Navigate triggered by result=null in QuizResultPage.
  const [interstitialSeen, setInterstitialSeen] = useState(() => {
    const saved = loadQuizState();
    if (saved && !saved.result) {
      restoreFromPersistence({
        currentStep: saved.currentStep,
        answers: saved.answers,
        result: null,
        startedAt: saved.startedAt ?? null,
      });
      return saved.currentStep > INTERSTITIAL_AFTER_STEP;
    }
    if (saved?.result) {
      // Stale completed-quiz state left in storage — purge it.
      clearQuizState();
    }
    return false;
  });

  // Persist every store change
  useEffect(() => {
    saveQuizState({ currentStep, answers, result, startedAt });
  }, [currentStep, answers, result, startedAt]);

  // Detect transition Q4 → Q5 → show interstitial once
  useEffect(() => {
    if (prevStepRef.current === null) {
      prevStepRef.current = currentStep;
      return;
    }
    const prev = prevStepRef.current;
    prevStepRef.current = currentStep;
    if (
      !interstitialSeen &&
      prev === INTERSTITIAL_AFTER_STEP &&
      currentStep === INTERSTITIAL_AFTER_STEP + 1
    ) {
      setPhase('interstitial');
      setInterstitialSeen(true);
    }
  }, [currentStep, interstitialSeen]);

  const handleComplete = useCallback(() => {
    setPhase('loading');
  }, []);

  const { data: session } = useSession();

  const handleLoadingDone = useCallback(() => {
    if (!countriesFile) {
      setPhase('questions');
      return;
    }
    // Stage 4.7 — pass the passport-treatment dataset so RU/BY/UA users
    // don't see countries where banking / property / documents are
    // effectively closed for them. Dataset is preloaded above; if it
    // hasn't resolved yet (rare slow networks), scoring still works —
    // treatment is just skipped this round and re-applied on result page.
    const r = scoreCountries(
      countriesFile.countries,
      answers,
      30,
      passportTreatment,
      visaExtra,
      taxRegimes,
      costOfLiving,
    );
    setResult(r);
    // Persist result to server side-effect when the user is already logged
    // in. Anonymous users have it in localStorage and will be synced on the
    // next login via useSyncQuizOnLogin. We don't await the request: a
    // network blip should not block the redirect to /quiz/result.
    if (session?.user) {
      void quizStateApi
        .upsert({ answers, result: r, startedAt })
        .catch(() => {
          // Silent — next session pickup will retry.
        });
    }
    // Skip email gate if user already gave email in a previous session.
    if (hasEmailBeenCaptured()) {
      navigate('/quiz/result');
    } else {
      setPhase('email-gate');
    }
  }, [countriesFile, answers, passportTreatment, visaExtra, taxRegimes, costOfLiving, setResult, navigate, session?.user, startedAt]);

  const handleEmailGateContinue = useCallback(() => {
    navigate('/quiz/result');
  }, [navigate]);

  const handleInterstitialContinue = useCallback(() => {
    setPhase('questions');
  }, []);

  const question = QUESTIONS[currentStep];

  return (
    <div className="relative flex min-h-screen flex-col bg-paper text-ink-700">
      <TopoBackground opacity={0.04} className="fixed inset-0 -z-10" />
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-ink-100 bg-paper/90 px-6 py-3 backdrop-blur-sm sm:px-10">
        <Link
          to="/"
          aria-label="Lodestar — home"
          className="rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terra-600"
        >
          <LodestarLogo size={28} />
        </Link>
        <div className="flex items-center gap-4">
          <Link
            to={lp('/countries')}
            className="hidden font-mono text-xs uppercase tracking-wider text-ink-500 hover:text-ink-900 sm:block"
          >
            {t('home.header.atlas', { defaultValue: 'Atlas' })}
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="relative mx-auto w-full max-w-3xl flex-1 px-4 py-10 sm:py-14">
        {phase === 'questions' && question && (
          <QuestionBackdrop questionId={question.id} />
        )}
        {phase === 'questions' && (
          <div className="relative">
            <div className="mb-8">
              <QuizProgress />
            </div>
            <QuestionStep question={question} onComplete={handleComplete} />
          </div>
        )}

        {phase === 'interstitial' && (
          <SocialProofInterstitial onContinue={handleInterstitialContinue} />
        )}

        {phase === 'loading' && (
          <LoadingScreen onDone={handleLoadingDone} durationMs={3000} />
        )}

        {phase === 'email-gate' && (
          <EmailGate onContinue={handleEmailGateContinue} />
        )}
      </main>

    </div>
  );
}
